import { deburr, get, isEmpty } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SidePanel from 'src/components/design-system/SidePanel';
import { useProductInformationPanel } from 'src/hooks/useProductInformationPanel';
import useWindowSize from 'src/hooks/useWindowSize';
import EditItemInformationDialog from 'src/sections/catalog_products/EditItemInformationDialog';
import ExpandedDescriptionView from 'src/sections/catalog_products/ExpandedDescriptionView';
import ProductInformationView from 'src/sections/catalog_products/ProductInformationView';
import { ProductData } from 'src/sections/catalog_products/useEditItemInformationForm';

import useGuardedAsyncCallback from 'src/hooks/useGuardedAsyncCallback';
import { useContextSelector } from 'use-context-selector';
import EnlargedImageView from './EnlargedImageView';
import { ViewCatalogProductsContext } from './ViewCatalogProductsContext';

interface ProductInformationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProductInformationPanel({ isOpen, onClose }: ProductInformationPanelProps) {
  const { t } = useTranslation();
  const { height, width } = useWindowSize();
  const {
    isImageEnlarged,
    isDescriptionExpanded,
    handleImageClick,
    handleCloseEnlargedImage,
    handleReadMoreClick,
    handleCloseDescription,
  } = useProductInformationPanel();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const editDialogCancelRef = useRef<(() => void) | null>(null);

  const updateProductSelection = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.updateProductSelection
  );

  const setSelectedProduct = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.setSelectedProduct
  );

  const loadingProductDetails = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.loadingProductDetails
  );

  const productDetails = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.productDetails
  );

  const selectedProduct = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.selectedProduct
  );

  const fetchProductDetails = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.fetchProductDetails
  );

  const fetchProducts = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.fetchProducts
  );

  const productSelections = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.productSelections
  );
  const isAddingToFavorites = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isAddingToFavorites
  );

  const handleSingleItemAddToCart = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleSingleItemAddToCart
  );

  const setIsSwapItemModalOpen = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.setIsSwapItemModalOpen
  );
  const setSwapItemData = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.setSwapItemData
  );

  const setQuantity = useCallback(
    (newQty: number) => {
      if (productDetails?.productKey) {
        updateProductSelection(productDetails.productKey, {
          qty: newQty,
          selected: newQty !== 0,
        });
      }
    },
    [updateProductSelection, productDetails?.productKey]
  );

  const { qty: quantity } = useMemo(
    () =>
      (productDetails?.productKey ? productSelections[productDetails.productKey] : null) || {
        selected: false,
        qty: 0,
      },
    [productSelections, productDetails?.productKey]
  );

  const folderOptions = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.folderOptions || []
  );

  const handleAddToFavorites = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleAddToFavorites
  );

  const getPanelTitle = useMemo(() => {
    if (isEditDialogOpen) {
      return t('Edit Item Information');
    }
    if (isDescriptionExpanded) {
      return t('Detailed Description');
    }
    if (isImageEnlarged) {
      return t('Item Image');
    }
    return t('Item Information');
  }, [isEditDialogOpen, isDescriptionExpanded, isImageEnlarged, t]);

  const handleEdit = useCallback(() => {
    setIsEditDialogOpen(true);
  }, []);

  const handleEditDialogClose = useCallback(() => {
    setIsEditDialogOpen(false);
  }, []);

  const handlePanelClose = useCallback(() => {
    if (isEditDialogOpen) {
      // Call the edit dialog's cancel handler which checks for unsaved changes
      if (editDialogCancelRef.current) {
        editDialogCancelRef.current();
      } else {
        // Fallback: just close if ref is not set
        handleEditDialogClose();
      }
      return;
    } else if (isDescriptionExpanded) {
      handleCloseDescription();
    } else if (isImageEnlarged) {
      handleCloseEnlargedImage();
    } else {
      if (!loadingProductDetails) {
        // Clear selected product when panel closes
        setSelectedProduct(null);
        onClose();
      }
    }
  }, [
    isEditDialogOpen,
    isDescriptionExpanded,
    isImageEnlarged,
    handleEditDialogClose,
    handleCloseDescription,
    handleCloseEnlargedImage,
    loadingProductDetails,
    setSelectedProduct,
    onClose,
  ]);

  const [handleAddToCartClick, isAddToCartPending] = useGuardedAsyncCallback(async () => {
    if (!productDetails) return;
    await handleSingleItemAddToCart({
      productId: productDetails.productId,
      catalogId: productDetails.catalogId,
      categoryId: productDetails.categoryId,
      quantity: quantity,
      isCompanySpecified: productDetails.isFormulary,
    });
    handlePanelClose();
  });

  // Convert ProductDetails to ProductData format for edit dialog
  const productDataForEdit: ProductData | null = useMemo(() => {
    if (!productDetails) return null;
    return {
      title: deburr(get(productDetails, 'description', '')).trim(),
      price: productDetails.price?.toString() || '',
      description: deburr(get(productDetails, 'info', '')).trim(),
      imageUrl: productDetails.url || '',
      productDetails: {
        'Item Name': deburr(get(productDetails, 'description', '')).trim(),
        Price: productDetails.price?.toString() || '',
        UPC: productDetails.upc || '',
        UNSPSC: productDetails.unspc || '',
      },
      manufacturingAndPackaging: {
        'Mfg Name': deburr(get(productDetails, 'manufacturerName', '')).trim(),
        'Mfg #': productDetails.manufacturerNumber || '',
        'Brand Name': deburr(get(productDetails, 'brandName', '')).trim(),
        UOM: productDetails.uom || '',
        'Case Pack Units': productDetails.casePack?.toString() || '',
        Weight: productDetails.weight || '',
      },
    };
  }, [productDetails]);

  const handleEditSaveSuccess = useCallback(async () => {
    await fetchProductDetails();
    await fetchProducts();
  }, [fetchProductDetails, fetchProducts]);

  const handleSwapItemClick = () => {
    if (!productDetails) return;
    setIsSwapItemModalOpen(true);
    setSwapItemData({
      pageType: 4,
      supplierId: productDetails.supplierId,
      supplierCompanyId: productDetails.supplierCompanyId,
      itemNumber: productDetails.itemNum,
      productId: productDetails.productId,
      catalogId: productDetails.catalogId,
      categoryId: productDetails.categoryId,
      quantity: quantity,
      price: Number(productDetails.price),
      uom: productDetails.uom,
    });
    onClose();
  };

  return (
    <SidePanel
      title={getPanelTitle}
      isOpen={isOpen}
      onClose={handlePanelClose}
      width={width < 900 ? `${width}px` : '900px'}
      position="right"
      className={`z-[1000] bg-gray-100 ${
        width < 900 ? 'pb-[16px]' : 'pb-0'
      } absolute top-0 right-0 z-[1000]`}
      titleClassName="bg-[#0E80B4] px-[32px] -mx-[32px] text-white -mt-[24px] py-[8px]"
    >
      <div className="bg-gray-100" style={{ height: `${height - 100}px` }}>
        {isEditDialogOpen && productDataForEdit ? (
          <EditItemInformationDialog
            isOpen={isEditDialogOpen}
            onClose={handleEditDialogClose}
            productData={productDataForEdit}
            selectedProduct={selectedProduct}
            onSaveSuccess={handleEditSaveSuccess}
            onRequestClose={(cancelHandler) => {
              editDialogCancelRef.current = cancelHandler;
            }}
          />
        ) : loadingProductDetails ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">{t('Loading item details...')}</div>
          </div>
        ) : isEmpty(productDetails) ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">{t('No item selected')}</div>
          </div>
        ) : isDescriptionExpanded ? (
          <ExpandedDescriptionView
            description={get(productDetails, 'info', '') as string}
            height={height}
            onClose={handleCloseDescription}
          />
        ) : isImageEnlarged ? (
          <EnlargedImageView
            imageUrl={get(productDetails, 'linkUrl', '') as string}
            title={get(productDetails, 'imageDescription', '') as string}
            height={height}
            onClose={handleCloseEnlargedImage}
          />
        ) : !isEmpty(productDetails) ? (
          <ProductInformationView
            productData={productDetails}
            quantity={quantity}
            onQuantityChange={setQuantity}
            onImageClick={handleImageClick}
            onReadMoreClick={handleReadMoreClick}
            onAddToCart={handleAddToCartClick}
            isAddToCartPending={isAddToCartPending}
            onAddToFavorites={handleAddToFavorites}
            onEdit={handleEdit}
            isLoading={loadingProductDetails || isAddingToFavorites}
            folderOptions={folderOptions}
            onSwapItemClick={handleSwapItemClick}
          />
        ) : null}
      </div>
    </SidePanel>
  );
}
